'use client'

import * as React from 'react'
import { cn } from '../../utils/cn'
import { Section } from '../layout/Section'
import { Container } from '../layout/Container'
import { Badge } from '../common/Badge'
import { FadeIn } from '../motion/FadeIn'
import { StaggerChildren } from '../motion/StaggerChildren'

interface Feature {
  icon: React.ReactNode
  title: string
  description: string
  badge?: string
}

interface FeatureGridProps {
  id?: string
  sectionNumber?: string
  heading: string
  subheading?: string
  features: Feature[]
  columns?: 2 | 3 | 4
}

const columnClasses = {
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-2 lg:grid-cols-3',
  4: 'md:grid-cols-2 lg:grid-cols-4',
}

export function FeatureGrid({
  id,
  sectionNumber,
  heading,
  subheading,
  features,
  columns = 3
}: FeatureGridProps) {
  return (
    <Section id={id} number={sectionNumber}>
      <Container>
        {/* Section Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <FadeIn>
            <h2 className="headline-lg mb-4">{heading}</h2>
          </FadeIn>
          {subheading && (
            <FadeIn delay={0.1}>
              <p className="body-lg">{subheading}</p>
            </FadeIn>
          )}
        </div>

        {/* Feature Grid */}
        <StaggerChildren
          className={cn('grid gap-6', columnClasses[columns])}
          staggerDelay={0.08}
        >
          {features.map((feature, index) => (
            <div key={index} className="landing-card-premium relative group h-full flex flex-col">
              {feature.badge && (
                <Badge variant="accent" size="sm" className="absolute top-4 right-4">
                  {feature.badge}
                </Badge>
              )}

              <div className="w-12 h-12 mb-5 rounded-xl icon-glow flex items-center justify-center text-[var(--accent)] transition-transform duration-200 group-hover:scale-105">
                {feature.icon}
              </div>

              <h3 className="text-lg font-semibold text-[var(--landing-text-primary)] mb-2">
                {feature.title}
              </h3>

              <p className="text-[15px] text-[var(--landing-text-secondary)] leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </StaggerChildren>
      </Container>
    </Section>
  )
}
